import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShoppingBag, LogIn, Loader2 } from "lucide-react";

const DEMO = [
  { label: "Super Admin", email: "superadmin@matrixshopping.com", password: "Admin@12345" },
  { label: "Merchant Owner", email: "owner@demomart.com", password: "Owner@12345" },
  { label: "Cashier", email: "cashier@demomart.com", password: "Cashier@12345" },
  { label: "Inventory Clerk", email: "clerk@demomart.com", password: "Clerk@12345" },
];

function routeFor(role) {
  if (role === "super_admin" || role === "support_admin") return "/admin";
  if (role === "cashier") return "/pos";
  return "/merchant";
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    const res = await login(email, password, "web-register");
    setLoading(false);
    if (res.ok) nav(routeFor(res.user.role));
    else setErr(res.error);
  };

  return (
    <div className="pos-dark min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-6 fade-up">
        <div className="hidden md:flex flex-col justify-between ms-card p-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center"><ShoppingBag className="text-white" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">MatrixShopping</h1>
                <p className="text-slate-400 text-sm">Point of Sale Platform</p>
              </div>
            </div>
            <p className="mt-8 text-slate-300 leading-relaxed">
              Offline-first multi-merchant POS with barcode sales, inventory, receipts, and one-click publishing to
              <span className="text-indigo-400 font-semibold"> GuyanaDiscounts.com</span>.
            </p>
          </div>
          <div className="text-xs text-slate-500 mono">GYD · Georgetown, Guyana</div>
        </div>

        <div className="ms-card p-8">
          <h2 className="text-xl font-bold mb-1">Sign in</h2>
          <p className="text-slate-400 text-sm mb-6">Access your register or dashboard</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-indigo-500"
                placeholder="you@business.com" autoComplete="username" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-indigo-500"
                placeholder="••••••••" autoComplete="current-password" />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="login-submit" disabled={loading}
              className="w-full touch-btn bg-indigo-600 hover:bg-indigo-500 rounded-lg py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />} Sign in
            </button>
          </form>
          <div className="mt-6">
            <p className="text-xs text-slate-500 mb-2">Quick demo accounts</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.email} data-testid={`demo-${d.label.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="text-left text-xs ms-card ms-card-hover px-3 py-2 rounded-lg">
                  <div className="font-semibold text-slate-200">{d.label}</div>
                  <div className="text-slate-500 truncate mono">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
